import { Field, Formik, Form } from 'formik';
import { Button } from '@/src/shadComponents/ui/button';
import { Input } from '@/src/shadComponents/ui/input';
import { Label } from '@/src/tailwindui/fieldset';
import { Field as FieldComponent } from '@/src/tailwindui/fieldset';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import useFTPSettings from './hooks/useFTPSettings';
import type { FTPGallerySettings } from './hooks/useFTPSettings';

interface FTPSettingsProps {
    galleryId: number;
}

export default function FTPSettings({ galleryId }: FTPSettingsProps) {
    const [isLoading, setIsLoading] = useState(true);
    const [settings, setSettings] = useState<FTPGallerySettings | null>(null);
    const { getFTPSettings, saveFTPSettingsWithPassword, listFTPDirectories } =
        useFTPSettings();

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const settings: FTPGallerySettings = await getFTPSettings(
                    galleryId,
                );
                if (isMounted) {
                    setSettings(settings);
                }
            } catch (error) {
                console.error(error);
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        load();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    if (isLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg">Loading FTP settings...</div>
            </div>
        );
    }

    if (!settings) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-lg text-red-600">
                    Failed to load FTP settings
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    FTP Sync Settings
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Configure FTP server settings for automatic photo
                    synchronization.
                </p>
            </div>

            <Formik
                initialValues={settings}
                onSubmit={async (values, actions) => {
                    const wasSuccessful = await saveFTPSettingsWithPassword(
                        galleryId,
                        values,
                    );
                    actions.setSubmitting(false);

                    if (wasSuccessful) {
                        toast.success('FTP settings saved successfully');
                    } else {
                        toast.error('Failed to save FTP settings');
                    }
                }}
            >
                {(props) => (
                    <Form
                        className="space-y-6"
                        placeholder={undefined}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                    >
                        {/* Connection Settings */}
                        <div className="space-y-4">
                            <div>
                                <h4 className="text-md font-medium text-gray-900 dark:text-white">
                                    Connection Settings
                                </h4>
                                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                    Configure your FTP server connection
                                    details.
                                </p>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <FieldComponent>
                                    <Label htmlFor="ftpHost">Host</Label>
                                    <Field
                                        as={Input}
                                        id="ftpHost"
                                        name="ftpHost"
                                        placeholder="ftp.example.com"
                                    />
                                </FieldComponent>

                                <FieldComponent>
                                    <Label htmlFor="ftpPort">Port</Label>
                                    <Field
                                        as={Input}
                                        id="ftpPort"
                                        name="ftpPort"
                                        placeholder="21, 22, or 990"
                                    />
                                </FieldComponent>
                            </div>

                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <FieldComponent>
                                    <Label htmlFor="ftpUser">Username</Label>
                                    <Field
                                        as={Input}
                                        id="ftpUser"
                                        name="ftpUser"
                                        placeholder="Enter FTP username"
                                    />
                                </FieldComponent>

                                <FieldComponent>
                                    <Label htmlFor="ftpPassword">
                                        Password
                                    </Label>
                                    <Field
                                        as={Input}
                                        id="ftpPassword"
                                        name="ftpPassword"
                                        type="password"
                                        placeholder="Enter FTP password"
                                    />
                                </FieldComponent>
                            </div>

                            <FieldComponent>
                                <Label htmlFor="ftpDirectory">Directory</Label>
                                <Field
                                    as={Input}
                                    id="ftpDirectory"
                                    name="ftpDirectory"
                                    placeholder="/path/to/upload/directory"
                                />
                            </FieldComponent>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => listFTPDirectories(galleryId)}
                            >
                                Test Connection
                            </Button>
                            <Button
                                type="submit"
                                disabled={props.isSubmitting}
                                className="bg-black hover:bg-gray-800 text-white"
                            >
                                {props.isSubmitting
                                    ? 'Saving...'
                                    : 'Save Settings'}
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
}
